// src/App.jsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { DarkModeProvider } from "./context/DarkModeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AutoLogout from "./components/AutoLogout";
import AdminLogin from "./pages/AdminLogin";
import FacultyLogin from "./pages/FacultyLogin";
import AdminDashboard from "./pages/AdminDashboard";
import FacultyDashboard from "./pages/FacultyDashboard";
import AdminSettings from "./pages/AdminSettings";
import FacultyProfile from "./pages/FacultyProfile";
import AdminReport from "./pages/AdminReport";
import FacultyReport from "./pages/FacultyReport";
import UserManagement from "./pages/UserManagement";
import CreateFaculty from "./pages/CreateFaculty";
import EditFaculty from "./pages/EditFaculty";
import StaffDetailsReport from "./pages/StaffDetailsReport";
import StaffAffidavitDCI from "./pages/StaffAffidavitDCI";
import SectionDetails from "./pages/SectionDetails";
import MetricsReport from "./pages/MetricsReport";
import ResetPassword from "./pages/ResetPassword";
import UserLogReport from "./pages/UserLogReport";
import DepartmentDetail from "./pages/DepartmentDetail";





function ProtectedRoute({ children, requiredRole }) {
  const { currentUser, userRole, adminProfile } = useAuth();
  if (!currentUser) return <Navigate to={requiredRole === "admin" ? "/admin" : "/login"} />;
  
  // If user is a superadmin or admin, they can access everything
  if (userRole === "admin" || userRole === "superadmin") return children;

  // Force first-time faculty to reset password before accessing anything
  if (userRole === "faculty" && adminProfile?.mustResetPassword === true) {
    return <Navigate to="/reset-password" />;
  }

  if (requiredRole === "both") return children;

  // If requiredRole is admin, check if faculty has any rights
  if (requiredRole === "admin") {
    const hasAnyRights = adminProfile?.rights && adminProfile.rights.length > 0;
    if (hasAnyRights) return children;
    return <Navigate to="/dashboard" />;
  }

  // Regular faculty access
  if (requiredRole === "faculty" && userRole !== "faculty") return <Navigate to="/admin/dashboard" />;
  
  return children;
}

function AppRoutes() {
  const { currentUser, userRole } = useAuth();
  return (
    <>
      <Routes>
        <Route path="/admin" element={!currentUser ? <AdminLogin /> : <Navigate to="/admin/dashboard" />} />
        <Route path="/login" element={!currentUser ? <FacultyLogin /> : <Navigate to="/dashboard" />} />
        <Route path="/reset-password" element={
          currentUser ? <ResetPassword /> : <Navigate to="/login" />
        } />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requiredRole="admin"><AdminSettings /></ProtectedRoute>
        } />
        <Route path="/admin/faculty/:id" element={
          <ProtectedRoute requiredRole="admin"><FacultyProfile /></ProtectedRoute>
        } />
        <Route path="/admin/report" element={
          <ProtectedRoute requiredRole="admin"><AdminReport /></ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute requiredRole="faculty"><FacultyDashboard /></ProtectedRoute>
        } />
        <Route path="/faculty/report" element={
          <ProtectedRoute requiredRole="faculty"><FacultyReport /></ProtectedRoute>
        } />
        <Route path="/my-portfolio" element={
          <ProtectedRoute requiredRole="faculty"><FacultyDashboard /></ProtectedRoute>
        } />
        <Route path="/profile/:id" element={
          <ProtectedRoute requiredRole="faculty"><FacultyProfile /></ProtectedRoute>
        } />
        <Route path="/my-report" element={
          <ProtectedRoute requiredRole="faculty"><FacultyReport /></ProtectedRoute>
        } />
        <Route path="/section/:sectionKey" element={
          <ProtectedRoute requiredRole="faculty"><SectionDetails /></ProtectedRoute>
        } />
        {/* Admin can also access section details with ?facultyId= param */}
        <Route path="/admin/section/:sectionKey" element={
          <ProtectedRoute requiredRole="admin"><SectionDetails /></ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRole="admin"><UserManagement /></ProtectedRoute>
        } />
        <Route path="/admin/create-faculty" element={
          <ProtectedRoute requiredRole="admin"><CreateFaculty /></ProtectedRoute>
        } />
        <Route path="/admin/edit-faculty/:id" element={
          <ProtectedRoute requiredRole="admin"><EditFaculty /></ProtectedRoute>
        } />
        <Route path="/admin/staff-details" element={
          <ProtectedRoute requiredRole="both"><StaffDetailsReport /></ProtectedRoute>
        } />
        <Route path="/admin/affidavit-dci" element={
          <ProtectedRoute requiredRole="both"><StaffAffidavitDCI /></ProtectedRoute>
        } />
        <Route path="/admin/metrics-report" element={
          <ProtectedRoute requiredRole="both"><MetricsReport /></ProtectedRoute>
        } />
        <Route path="/admin/user-logs" element={
          <ProtectedRoute requiredRole="admin"><UserLogReport /></ProtectedRoute>
        } />
        <Route path="/admin/department" element={
          <ProtectedRoute requiredRole="admin"><DepartmentDetail /></ProtectedRoute>
        } />
        <Route path="/admin/departments/detail" element={
          <ProtectedRoute requiredRole="admin"><DepartmentDetail /></ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to={currentUser ? (userRole === "admin" || userRole === "superadmin" ? "/admin/dashboard" : "/dashboard") : "/login"} />} />

      </Routes>
    </>
  );
}

export default function App() {
  return (
    <DarkModeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AutoLogout />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </DarkModeProvider>
  );
}
